import React, { useEffect, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface ProductGrade {
    id_product_grade: number;
    product_grade_name: string;
}

interface GradeValue {
    quantity: number;
    amount: number;
}

interface MonthlyRow {
    month: string;
    grades: { [gradeId: string]: GradeValue };
    ungraded: GradeValue;
    total: GradeValue;
}

interface YearlyQuantityGradeData {
    grades: ProductGrade[];
    has_ungraded: boolean;
    months: MonthlyRow[];
    grand_totals: {
        grades: { [gradeId: string]: GradeValue };
        ungraded: GradeValue;
        total: GradeValue;
    };
    period_label: string;
}

const getDefaultFiscalRange = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const startYear = month >= 7 ? year : year - 1;

    return {
        fromDate: `${startYear}-07`,
        toDate: `${startYear + 1}-06`,
    };
};

const YearlyQuantityGradeReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const defaultRange = getDefaultFiscalRange();

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<YearlyQuantityGradeData | null>(null);
    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? defaultRange.fromDate,
        toDate: query.get('toDate') ?? defaultRange.toDate,
        includeFreeItem: query.get('includeFreeItem') === 'true',
    });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? String(projectObj.value) : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setFormData((prev) => ({
            ...prev,
            idProjects: selected ? String(selected.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (formData.fromDate > formData.toDate) {
            toast.error('To Date must be after or equal to From Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'mis-reports/yearly-sales-grade-report',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.data);
            } else {
                setReportData(null);
                toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="month"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="month"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="includeFreeItem">
                                    <Form.Label className='d-block'>&nbsp;</Form.Label>
                                    <div className="form-check mt-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="includeFreeItemQuantity"
                                            name="includeFreeItem"
                                            label="Include Free Item"
                                            checked={formData.includeFreeItem}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span><i className='fas fa-eye'></i> View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    {reportData ? (
                        <>
                            <div className="mb-3">
                                <strong>Period:</strong> {reportData.period_label}
                            </div>
                            <Table responsive striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        {reportData.grades.map((grade) => (
                                            <th key={grade.id_product_grade} className="text-end">
                                                {grade.product_grade_name}
                                            </th>
                                        ))}
                                        {reportData.has_ungraded && (
                                            <th className="text-end">Ungraded</th>
                                        )}
                                        <th className="text-end">Total Quantity</th>
                                        <th className="text-end">Total Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.months.map((row) => (
                                        <tr key={row.month}>
                                            <td>{row.month}</td>
                                            {reportData.grades.map((grade) => {
                                                const gradeData = row.grades[String(grade.id_product_grade)] ?? { quantity: 0, amount: 0 };
                                                return (
                                                    <td key={grade.id_product_grade} className="text-end">
                                                        {numberFormat(gradeData.quantity)}
                                                    </td>
                                                );
                                            })}
                                            {reportData.has_ungraded && (
                                                <td className="text-end">{numberFormat(row.ungraded.quantity)}</td>
                                            )}
                                            <td className="text-end">{numberFormat(row.total.quantity)}</td>
                                            <td className="text-end">{numberFormat(row.total.amount)}</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td><strong>Grand Total</strong></td>
                                        {reportData.grades.map((grade) => {
                                            const gradeData = reportData.grand_totals.grades[String(grade.id_product_grade)] ?? { quantity: 0, amount: 0 };
                                            return (
                                                <td key={grade.id_product_grade} className="text-end">
                                                    <strong>{numberFormat(gradeData.quantity)}</strong>
                                                </td>
                                            );
                                        })}
                                        {reportData.has_ungraded && (
                                            <td className="text-end">
                                                <strong>{numberFormat(reportData.grand_totals.ungraded.quantity)}</strong>
                                            </td>
                                        )}
                                        <td className="text-end">
                                            <strong>{numberFormat(reportData.grand_totals.total.quantity)}</strong>
                                        </td>
                                        <td className="text-end">
                                            <strong>{numberFormat(reportData.grand_totals.total.amount)}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </>
                    ) : (
                        <div>No data available</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default YearlyQuantityGradeReport;
